import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faLock,
  faEye,
  faEyeSlash,
  faArrowRight,
  faCircleCheck,
  faKey,
  faCalendarCheck,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './Login.css';
import useOnlineStatus, { NO_INTERNET_MSG, SERVER_MSG, REQUEST_TIMEOUT_MS, wakeServer, reasonOf, withRetry } from './useOnlineStatus';

const ROLE_ROUTES = {
  1: '/admin/dashboard',
  2: '/teacher/myclasses',
  3: '/student/dashboard',
  4: '/sales/dashboard',
};

// Reads the JSON error body safely (some server errors are not JSON)
const readJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const online = useOnlineStatus();

  // wake the server while the person types
  useEffect(() => {
    wakeServer();
  }, []);

  // Clear the "no internet" message as soon as the connection comes back
  useEffect(() => {
    if (online) setError((prev) => (prev === NO_INTERNET_MSG ? '' : prev));
  }, [online]);
  const shownError = error || (!online ? NO_INTERNET_MSG : '');
  const EMAIL = process.env.REACT_APP_EMAIL;
  const PHONE = process.env.REACT_APP_MOBILE_NUMBER1;

  const resetUrl = `https://api.whatsapp.com/send/?phone=${PHONE}&text=${encodeURIComponent(
    'Hi! I forgot my RoboMind password. Can you help me reset it?'
  )}&type=phone_number&app_absent=0`;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.username.trim() || !formData.password) {
      setError('Please enter your username and password.');
      return;
    }

    if (!navigator.onLine) {
      setError(NO_INTERNET_MSG);
      return;
    }

    setLoading(true);
    // one request, with its own timeout
    const send = async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        return await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/login`, {
          method: 'POST',
          signal: controller.signal,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, username: formData.username.trim() }),
        });
      } finally {
        clearTimeout(timer);
      }
    };
    try {
      // fetch only throws when there was no answer at all: try once more in that case
      const response = await withRetry(send, () => true);

      if (response.status === 401) {
        const data = await readJson(response);
        setError(data.error || 'Wrong username or password.');
        return;
      }

      if (response.status === 403) {
        const data = await readJson(response);
        setError(data.error || 'This account is inactive. Please contact us.');
        return;
      }

      if (!response.ok) {
        const data = await readJson(response);
        setError(data.error || 'Login failed. Please try again.');
        return;
      }

      const data = await response.json();
      const role = data.user.role;

      // Same storage as before, so the dashboards keep working
      localStorage.setItem('userData', JSON.stringify(data.token));
      setSuccess(true);

      setTimeout(() => navigate(ROLE_ROUTES[role] || '/'), 900);
    } catch (err) {
      // No answer at all. Only say "no internet" when the device really is offline.
      console.error('Login error:', err);
      setError(navigator.onLine ? `${SERVER_MSG} (${reasonOf(err)})` : NO_INTERNET_MSG);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="rmlogin" aria-labelledby="rmlogin-title">
      <div className="rmlogin-inner">
        {/* Sign-in form (first on every screen size) */}
        <form className="rmlogin-card" onSubmit={handleSubmit} noValidate>
          <span className="rmlogin-eyebrow">Students & teachers</span>
          <h1 id="rmlogin-title" className="rmlogin-title">
            Welcome <span>back</span>
          </h1>
          <p className="rmlogin-sub">Sign in to see your classes, projects and progress.</p>

          {success ? (
            <div className="rmlogin-success" role="status">
              <FontAwesomeIcon icon={faCircleCheck} />
              Signed in! Opening your dashboard…
            </div>
          ) : (
            <>
              <label htmlFor="rmlogin-username" className="rmlogin-label">
                Username
              </label>
              <div className="rmlogin-field">
                <FontAwesomeIcon icon={faUser} className="rmlogin-field-icon" />
                <input
                  id="rmlogin-username"
                  name="username"
                  type="text"
                  className="rmlogin-input"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck="false"
                  placeholder="Your username"
                  value={formData.username}
                  onChange={handleChange}
                  aria-invalid={Boolean(shownError)}
                  aria-describedby={shownError ? 'rmlogin-error' : undefined}
                />
              </div>

              <label htmlFor="rmlogin-password" className="rmlogin-label">
                Password
              </label>
              <div className="rmlogin-field">
                <FontAwesomeIcon icon={faLock} className="rmlogin-field-icon" />
                <input
                  id="rmlogin-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  className="rmlogin-input rmlogin-input--pass"
                  autoComplete="current-password"
                  placeholder="Your password"
                  value={formData.password}
                  onChange={handleChange}
                  aria-invalid={Boolean(shownError)}
                  aria-describedby={shownError ? 'rmlogin-error' : undefined}
                />
                <button
                  type="button"
                  className="rmlogin-eye"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>

              {shownError && (
                <p id="rmlogin-error" className="rmlogin-error" role="alert">
                  {shownError}
                </p>
              )}

              <button type="submit" className="rmlogin-btn" disabled={loading}>
                {loading ? (
                  <span className="rmlogin-spinner" aria-label="Signing in" />
                ) : (
                  <>
                    Sign in
                    <FontAwesomeIcon icon={faArrowRight} />
                  </>
                )}
              </button>
            </>
          )}

          <div className="rmlogin-help">
            <a href={resetUrl} target="_blank" rel="noopener noreferrer" className="rmlogin-help-link">
              <FontAwesomeIcon icon={faWhatsapp} />
              Forgot password? Message us
            </a>
            {EMAIL && (
              <span>
                or email <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
              </span>
            )}
          </div>
        </form>

        {/* Not a student yet */}
        <aside className="rmlogin-side" aria-labelledby="rmlogin-side-title">
          <h2 id="rmlogin-side-title" className="rmlogin-side-title">
            Not a student yet?
          </h2>

          <Link to="/joinfreeclass" className="rmlogin-option">
            <span className="rmlogin-option-icon">
              <FontAwesomeIcon icon={faKey} />
            </span>
            <span className="rmlogin-option-text">
              <strong>Join your free class</strong>
              <span>Already booked? Enter the code we sent you.</span>
            </span>
            <FontAwesomeIcon icon={faArrowRight} className="rmlogin-option-arrow" />
          </Link>

          <Link to="/FreeTrial" className="rmlogin-option">
            <span className="rmlogin-option-icon">
              <FontAwesomeIcon icon={faCalendarCheck} />
            </span>
            <span className="rmlogin-option-text">
              <strong>Book a free trial class</strong>
              <span>1-to-1, online, for ages 6–17.</span>
            </span>
            <FontAwesomeIcon icon={faArrowRight} className="rmlogin-option-arrow" />
          </Link>
        </aside>
      </div>
    </section>
  );
};

export default Login;